import { ArrowDown, ArrowUp } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { usePlayer } from '@/audio/store'
import { Button, IconButton } from '@/components/ui'
import { cn } from '@/lib/cn'
import { QueueUpNextRow } from './QueueUpNextRow'
import { useDragToReorder } from './useDragToReorder'
import { SimilarQueue } from './SimilarQueue'
import { SwipeReveal } from './SwipeSurface'
import { removeUpcoming } from './queueGestureActions'

export interface QueueUpNextProps {
  /** Index in the full queue that this list starts at. */
  offset: number
  className?: string
}

/** Shared by the queue sheet and expanded player. Only upcoming songs can be reordered. */
export function QueueUpNext({ offset, className }: QueueUpNextProps) {
  const queue = usePlayer((state) => state.queue)
  const playAt = usePlayer((state) => state.playAt)
  const moveInQueue = usePlayer((state) => state.moveInQueue)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const editControls = useRef<HTMLDivElement>(null)
  const items = queue.slice(offset)
  const editingIndex = items.findIndex((item) => item.uid === editingId)
  const editing = items[editingIndex]

  const move = (from: number, to: number) => {
    if (!items[from] || to < 0 || to >= items.length || from === to) return
    moveInQueue(offset + from, offset + to)
    setAnnouncement(`${items[from].title} moved to position ${to + 1} of ${items.length} in Up next.`)
  }
  const { listRef, bindList, drag, swipe, restingOffset } = useDragToReorder({
    ids: items.map((item) => item.uid),
    onMove: move,
    onRemove: (index) => removeUpcoming(items[index].uid),
    enabled: !editing,
  })

  useEffect(() => { if (editingIndex < 0) setEditingId(null) }, [editingIndex])

  function finishEditing() {
    const row = Array.from(listRef.current?.children ?? []).find((row) => row.getAttribute('data-queue-row') === editingId)
    setEditingId(null)
    row?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true })
  }

  return <>
    <p role="status" aria-live="polite" className="sr-only">{drag ? `${items[drag.activeIndex]?.title ?? 'Song'} lifted. Position ${drag.overIndex + 1} of ${items.length}.` : announcement}</p>
    {!items.length ? <p className="px-2 py-3 text-sm text-text-subtle">No more queued songs.</p> : <>
      {editing ? <div ref={editControls} tabIndex={-1} aria-label={`Reorder ${editing.title}`} className="mb-2 rounded-lg border border-accent/30 bg-accent-soft p-3"
        onKeyDownCapture={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); move(editingIndex, editingIndex + (event.key === 'ArrowUp' ? -1 : 1)) }
          if (event.key === 'Escape' || (event.key === 'Enter' && event.target === event.currentTarget)) { event.preventDefault(); event.stopPropagation(); finishEditing() }
        }}>
        <p className="truncate text-sm font-medium text-text">Reordering {editing.title}</p>
        <div className="mt-1 flex items-center gap-1">
          <span className="min-w-0 flex-1 text-xs text-text-muted">{editingIndex + 1} of {items.length} · Use arrows to move</span>
          <IconButton aria-label={`Move ${editing.title} up`} disabled={editingIndex === 0} onClick={() => move(editingIndex, editingIndex - 1)}><ArrowUp className="h-4 w-4" aria-hidden="true" /></IconButton>
          <IconButton aria-label={`Move ${editing.title} down`} disabled={editingIndex === items.length - 1} onClick={() => move(editingIndex, editingIndex + 1)}><ArrowDown className="h-4 w-4" aria-hidden="true" /></IconButton>
          <Button variant="ghost" size="sm" onClick={finishEditing}>Done</Button>
        </div>
      </div> : items.length > 1 ? <p className="px-2 pb-2 text-xs text-text-subtle">Hold a song to reorder.</p> : null}
      <ul ref={bindList} className={cn('flex flex-col gap-1', className)} aria-label="Up next songs">
        {items.map((item, position) => {
          const dragging = drag?.activeIndex === position
          const shift = dragging ? drag.offsetY : restingOffset(position)
          return <li key={item.uid} data-queue-row={item.uid}
            className={dragging ? 'relative z-10 select-none' : 'relative select-none'}
            style={{ transform: shift ? `translateY(${shift}px)` : undefined, transition: dragging ? 'none' : 'transform 160ms ease', WebkitTouchCallout: 'none' }}>
            <SwipeReveal offset={swipe?.index === position ? swipe.offset : 0} left="Remove" right="Already queued">
            <QueueUpNextRow item={item} position={position} count={items.length} dragging={dragging} editing={item.uid === editingId}
              onPlay={() => playAt(offset + position)} onRemove={() => removeUpcoming(item.uid)}
              onMoveToTop={() => move(position, 0)} onReorder={() => setEditingId(item.uid)} onReorderFocus={() => editControls.current?.focus()} />
            </SwipeReveal>
          </li>
        })}
      </ul>
    </>}
    <SimilarQueue />
  </>
}
